import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, PencilLine, Search } from 'lucide-react';
import { SifaMitarbeiter, SifaTeilnahme, SifaUnterweisung } from '../../services/sifa/types';
import { faelligkeitsStufe } from '../../services/sifa/teilnahmeLogic';
import { AMPEL, AMPEL_RANG, LEERER_STAND, ampelStufe, standJePerson, sucheMitarbeiter } from './mitarbeiterModulLogic';
import { ergebnisText, formatDatum, formatZeit } from './unterweisungModulLogic';

interface Props {
  mitarbeiter: SifaMitarbeiter[];
  teilnahmen: SifaTeilnahme[];
  unterweisungen: SifaUnterweisung[];
  laden: boolean;
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Auswahl fuer „Zusammenführen" — genau zwei Zeilen. */
  auswahl: Set<string>;
  onAuswahl: (id: string) => void;
  onBearbeiten: (m: SifaMitarbeiter) => void;
}

/**
 * Der Mitarbeiterstamm als Tabelle mit Suche, Faelligkeitsampel und
 * aufklappbarer Historie je Person.
 *
 * Die Ampel haengt an der juengsten BESTANDENEN Teilnahme (standJePerson):
 * rot = abgelaufen, gelb = laeuft in 60 Tagen ab, gruen = gueltig, grau =
 * kein Nachweis. Sortiert wird nach Dringlichkeit, damit die Liste oben
 * zeigt, wen die SiFa als Naechstes anschreiben muss.
 */
export const MitarbeiterListe: React.FC<Props> = ({
  mitarbeiter,
  teilnahmen,
  unterweisungen,
  laden,
  isReadOnly = false,
  isMobile = false,
  auswahl,
  onAuswahl,
  onBearbeiten,
}) => {
  const [suche, setSuche] = useState('');
  const [offenId, setOffenId] = useState<string | null>(null);
  const [nachAmpel, setNachAmpel] = useState(false);

  const stand = useMemo(() => standJePerson(teilnahmen), [teilnahmen]);
  const stufeVon = (m: SifaMitarbeiter) => ampelStufe(stand[m.id] || LEERER_STAND, (g) => faelligkeitsStufe(g));
  // Ohne useMemo: ein Kundenstamm hat Dutzende Zeilen, kein Tausend — und der
  // Sortierschluessel haengt an `stufeVon`, das je Render neu entsteht.
  const gefiltert = sucheMitarbeiter(mitarbeiter, suche);
  const sichtbar = nachAmpel
    ? [...gefiltert].sort((a, b) => AMPEL_RANG[stufeVon(a)] - AMPEL_RANG[stufeVon(b)])
    : gefiltert;

  const titelVon = (id: string) => {
    const u = unterweisungen.find((x) => x.id === id);
    return u ? `Nr. ${u.nummer} ${u.titel}` : '—';
  };

  const ampel = (m: SifaMitarbeiter) => {
    const s = stufeVon(m);
    const a = AMPEL[s];
    const g = stand[m.id]?.gueltigBis;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-bold border rounded-full ${a.klasse}`}>
        <span className={`w-2 h-2 rounded-full ${a.punkt}`} />
        {a.label}{g && s !== 'ohne' ? ` · ${formatDatum(g)}` : ''}
      </span>
    );
  };

  const historie = (m: SifaMitarbeiter) => {
    const h = stand[m.id]?.historie || [];
    if (h.length === 0) return <p className="text-xs text-slate-500">Noch keine Teilnahme dieser Person.</p>;
    return (
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="px-2 py-1 text-left font-bold">Datum</th>
            <th className="px-2 py-1 text-left font-bold">Kurs</th>
            <th className="px-2 py-1 text-left font-bold">Ergebnis</th>
            <th className="px-2 py-1 text-left font-bold">Zertifikat</th>
            <th className="px-2 py-1 text-left font-bold">Gültig bis</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {h.map((t) => (
            <tr key={t.id}>
              <td className="px-2 py-1 text-slate-600 whitespace-nowrap">{formatZeit(t.abgeschlossenAm || t.begonnenAm)}</td>
              <td className="px-2 py-1 text-slate-600">{titelVon(t.unterweisungId)}</td>
              <td className="px-2 py-1 text-slate-600">{ergebnisText(t)}</td>
              <td className="px-2 py-1 text-slate-600 font-mono">{t.zertifikatNr || '—'}</td>
              <td className="px-2 py-1 text-slate-600">{t.gueltigBis ? formatDatum(t.gueltigBis) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name, E-Mail, Abteilung, Personalnummer…" className="w-full pl-9 pr-3 py-2 min-h-[40px] text-sm bg-white border border-slate-200 rounded-xl outline-none focus:ring-1 focus:ring-teal-300" />
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={nachAmpel} onChange={(e) => setNachAmpel(e.target.checked)} className="w-4 h-4 accent-teal-600" />
          Nach Fälligkeit sortieren
        </label>
        <span className="text-xs text-slate-500">{sichtbar.length} von {mitarbeiter.length}</span>
      </div>

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
      ) : sichtbar.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">{mitarbeiter.length === 0 ? 'Noch kein Mitarbeiter im Stamm' : 'Kein Treffer'}</p>
          {mitarbeiter.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">
              Personen entstehen von selbst, sobald jemand eine Unterweisung abschließt — oder hier über „Anlegen" und „CSV-Import".
            </p>
          )}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{m.nachname}, {m.vorname}</p>
                  <p className="text-xs text-slate-500 truncate">{[m.geburtsdatum ? formatDatum(m.geburtsdatum) : null, m.email, m.abteilung].filter(Boolean).join(' · ') || '—'}</p>
                </div>
                <button onClick={() => onBearbeiten(m)} className="p-2 text-blue-700 hover:bg-blue-50 rounded-lg"><PencilLine className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between gap-2">
                {ampel(m)}
                <button onClick={() => setOffenId(offenId === m.id ? null : m.id)} className="text-xs font-bold text-slate-600 flex items-center gap-1">
                  Historie {offenId === m.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
              {offenId === m.id && <div className="pt-1 overflow-x-auto">{historie(m)}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                {!isReadOnly && <th className="px-3 py-2 w-8" title='Für „Zusammenführen" zwei Zeilen wählen'></th>}
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 text-left font-bold">Name</th>
                <th className="px-3 py-2 text-left font-bold">Geburtsdatum</th>
                <th className="px-3 py-2 text-left font-bold">E-Mail</th>
                <th className="px-3 py-2 text-left font-bold">Abteilung</th>
                <th className="px-3 py-2 text-left font-bold">Letzte Unterweisung</th>
                <th className="px-3 py-2 text-left font-bold">Gültig bis</th>
                <th className="px-3 py-2 text-left font-bold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((m) => {
                const s = stand[m.id] || LEERER_STAND;
                const offen = offenId === m.id;
                return (
                  <React.Fragment key={m.id}>
                    <tr className="hover:bg-slate-50/60">
                      {!isReadOnly && (
                        <td className="px-3 py-2"><input type="checkbox" checked={auswahl.has(m.id)} onChange={() => onAuswahl(m.id)} className="w-4 h-4 accent-cyan-600" /></td>
                      )}
                      <td className="px-3 py-2">
                        <button onClick={() => setOffenId(offen ? null : m.id)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Historie">
                          {offen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className={`px-3 py-2 font-semibold ${m.aktiv === false ? 'text-slate-400' : 'text-slate-800'}`}>{m.nachname}, {m.vorname}{m.personalnummer ? <span className="text-slate-400 font-normal"> · {m.personalnummer}</span> : null}{m.aktiv === false && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200" title="Vom Betrieb im Kundenbereich deaktiviert">Inaktiv</span>}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{m.geburtsdatum ? formatDatum(m.geburtsdatum) : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600 break-all">{m.email || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{m.abteilung || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        {s.letzte ? <>{formatDatum(s.letzte.abgeschlossenAm)} <span className="text-slate-400">· {titelVon(s.letzte.unterweisungId)}</span></> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2">{ampel(m)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => onBearbeiten(m)} className="min-h-[36px] px-2.5 flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">
                          <PencilLine className="w-3.5 h-3.5" /> {isReadOnly ? 'Ansehen' : 'Bearbeiten'}
                        </button>
                      </td>
                    </tr>
                    {offen && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={isReadOnly ? 8 : 9} className="px-6 py-3">{historie(m)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
